import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";

export default function PrivacyPolicy() {
  const lastUpdated = "January 21, 2026";
  
  useEffect(() => {
    document.title = "Privacy Policy | Eatzy Kudoz";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Eatzy Kudoz Privacy Policy - Learn how we collect, use, and protect your personal information when using our restaurant discovery and rewards platform.");
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = "Eatzy Kudoz Privacy Policy - Learn how we collect, use, and protect your personal information when using our restaurant discovery and rewards platform.";
      document.head.appendChild(meta);
    }
    
    return () => {
      document.title = "Eatzy Kudoz";
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link href="/login">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

            <div className="prose prose-sm max-w-none dark:prose-invert space-y-6">
              <section>
                <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to Eatzy Kudoz ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application, website, and related services (collectively, the "Platform").
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  By using Eatzy Kudoz, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Platform.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">2.1 Information You Provide</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, phone number, username, and password when you create an account.</li>
                  <li><strong>Profile Information:</strong> Profile photo, bio, food preferences, and dietary restrictions you choose to share.</li>
                  <li><strong>Review Content:</strong> Restaurant reviews, ratings, photos, videos, and comments you post on our Platform.</li>
                  <li><strong>Order Information:</strong> Delivery addresses, payment information, and order history when you place food orders through our Platform.</li>
                  <li><strong>Communications:</strong> Messages, feedback, and correspondence you send to us or other users.</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">2.2 Information Collected Automatically</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Location Data:</strong> With your permission, we collect precise or approximate location information to show nearby restaurants and personalize your experience.</li>
                  <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, browser type, and mobile network information.</li>
                  <li><strong>Usage Data:</strong> Pages visited, features used, search queries, restaurants viewed, and interaction patterns.</li>
                  <li><strong>Cookies and Similar Technologies:</strong> We use cookies, pixels, and similar technologies to enhance your experience and gather analytics.</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">2.3 Information from Third Parties</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Social Media:</strong> If you connect your social media accounts (Facebook, Instagram, Google, Apple), we may receive profile information from those platforms.</li>
                  <li><strong>Eatzy Platform:</strong> We integrate with the Eatzy ordering platform to facilitate food orders and may receive order-related information.</li>
                  <li><strong>Restaurant Partners:</strong> Information about your orders and dining experiences from our restaurant partners.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">We use the information we collect to:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Provide, maintain, and improve our Platform and services</li>
                  <li>Process and fulfill food orders through our restaurant partners</li>
                  <li>Manage the Kudoz rewards program, including tracking points earned and redeemed</li>
                  <li>Personalize your experience with restaurant recommendations based on your preferences and location</li>
                  <li>Display your reviews and content to other users</li>
                  <li>Enable social features and connections with other food enthusiasts</li>
                  <li>Send you notifications about your account, orders, rewards, and platform updates</li>
                  <li>Respond to your comments, questions, and customer service requests</li>
                  <li>Monitor and analyze trends, usage, and activities on our Platform</li>
                  <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
                  <li>Comply with legal obligations and enforce our terms of service</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">4. How We Share Your Information</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">4.1 Public Information</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Your profile information, reviews, ratings, photos, and comments are publicly visible to other users of the Platform. Your username and profile photo will be associated with your content.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">4.2 Restaurant Partners</h3>
                <p className="text-muted-foreground leading-relaxed">
                  When you place an order or leave a review, we share relevant information with the restaurant, including your name, order details, delivery address, and review content.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">4.3 Service Providers</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We share information with third-party vendors who perform services on our behalf, including payment processing, data analytics, email delivery, hosting services, and customer service.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">4.4 Social Media Cross-Posting</h3>
                <p className="text-muted-foreground leading-relaxed">
                  If you choose to cross-post your content to connected social media accounts (Facebook, Instagram), your content will be shared on those platforms according to their respective privacy policies.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">4.5 Legal Requirements</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We may disclose your information if required by law, regulation, legal process, or governmental request, or to protect the rights, property, or safety of Eatzy Kudoz, our users, or others.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">4.6 Business Transfers</h3>
                <p className="text-muted-foreground leading-relaxed">
                  In connection with any merger, acquisition, or sale of company assets, your information may be transferred as part of that transaction.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">5. Kudoz Rewards Program</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The Kudoz rewards program tracks your engagement activities to award points. We collect and process information about:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                  <li>Reviews and ratings you submit</li>
                  <li>Photos and videos you upload</li>
                  <li>Social interactions (likes, comments, shares)</li>
                  <li>Referrals and invitations</li>
                  <li>Orders placed through the Platform</li>
                  <li>Daily engagement and streaks</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  Your Kudoz balance, transaction history, and earned badges may be visible on your public profile.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">6. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure authentication mechanisms</li>
                  <li>Regular security assessments and monitoring</li>
                  <li>Access controls limiting who can view your data</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">7. Your Rights and Choices</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">Depending on your location, you may have the following rights:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                  <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information.</li>
                  <li><strong>Deletion:</strong> Request that we delete your personal information, subject to certain exceptions.</li>
                  <li><strong>Portability:</strong> Request a copy of your data in a portable format.</li>
                  <li><strong>Opt-Out:</strong> Opt out of marketing communications and certain data processing activities.</li>
                  <li><strong>Location Services:</strong> Disable location services through your device settings.</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  To exercise these rights, please contact us using the information provided below. We will respond to your request within the timeframe required by applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">8. Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We retain your personal information for as long as your account is active or as needed to provide you services. We may also retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. Review content may remain on the Platform even after account deletion to maintain the integrity of our review system.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">9. Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Eatzy Kudoz is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">10. Third-Party Links and Services</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our Platform may contain links to third-party websites and services, including restaurant websites, social media platforms, and payment processors. This Privacy Policy does not apply to those third-party services. We encourage you to review the privacy policies of any third-party services you access.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">11. International Data Transfers</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our Platform, you consent to the transfer of your information to these countries.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">12. Changes to This Privacy Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically for any changes.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">13. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <div className="mt-3 p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium">Eatzy Kudoz</p>
                  <p className="text-muted-foreground">Email: privacy@eatzy.com</p>
                  <p className="text-muted-foreground">Website: www.eatzy.com</p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link href="/terms">
            <Button variant="ghost" className="text-muted-foreground underline">
              View Terms of Service
            </Button>
          </Link>
        </div>

        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Eatzy Kudoz. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
